import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

const HeroSection = React.memo(({ heroData, loading }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // التبديل التلقائي بين صور البانر
  useEffect(() => {
    if (heroData.backgroundImages && heroData.backgroundImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) =>
          (prevIndex + 1) % heroData.backgroundImages.length
        );
      }, heroData.slideInterval || 5000);

      return () => clearInterval(interval);
    }
  }, [heroData.backgroundImages, heroData.slideInterval]);

  // دوال للتحكم في الـ slider
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      (prevIndex + 1) % (heroData.backgroundImages?.length || 1)
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? (heroData.backgroundImages?.length || 1) - 1 : prevIndex - 1
    );
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <section
      className={`relative min-h-[500px] sm:min-h-[600px] md:h-screen flex items-center justify-center overflow-hidden bg-black`}
    >
      {/* صور الخلفية - Slider */}
      {heroData.backgroundImages && heroData.backgroundImages.length > 0 ? (
        heroData.backgroundImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        ))
      ) : null}

      {/* Overlay للتأكد من وضوح النص */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
        {/* الشعار */}
        <div className="mb-4 sm:mb-6">
          <img
            src="/3.png"
            alt="شعار الموقع"
            className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 mx-auto object-contain drop-shadow-2xl"
          />
        </div>

        {/* العناوين */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 text-white leading-tight drop-shadow-2xl">
          {heroData.title}
        </h1>

        <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-brand-orange-500 mb-3 sm:mb-4 drop-shadow-lg font-semibold">
          {heroData.subtitle}
        </p>

        <p className="text-sm sm:text-base md:text-lg text-gray-200 mb-6 sm:mb-8 max-w-3xl mx-auto drop-shadow-lg">
          {heroData.description}
        </p>

        {/* الأزرار */}
        <div className="flex flex-wrap gap-3 justify-center mb-8">
          <button
            onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
            className="bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-bold px-6 py-3 sm:px-8 sm:py-4 rounded-full flex items-center gap-2 transition transform hover:scale-105 shadow-xl"
          >
            {heroData.buttonText} <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button className="bg-white/10 hover:bg-white/20 backdrop-blur text-white font-bold px-6 py-3 sm:px-8 sm:py-4 rounded-full border-2 border-brand-gray-600 transition shadow-xl hover:border-brand-blue-500">
            استكشف الخدمات
          </button>
        </div>

        {/* Dots للصور - يظهر فقط إذا كان هناك أكثر من صورة */}
        {heroData.backgroundImages && heroData.backgroundImages.length > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            {heroData.backgroundImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToImage(idx)}
                className={`h-2 rounded-full transition-all ${idx === currentImageIndex ? 'w-8 bg-brand-orange-600 led-glow' : 'w-2 bg-white/50 hover:bg-white/70'
                  }`}
                title={`الصورة ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* أسهم التحكم - تظهر فقط إذا كان هناك أكثر من صورة */}
      {heroData.backgroundImages && heroData.backgroundImages.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-full transition z-20 shadow-lg hover:scale-110"
            title="الصورة السابقة"
          >
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 rotate-180" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-full transition z-20 shadow-lg hover:scale-110"
            title="الصورة التالية"
          >
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </>
      )}
    </section>
  );
});

HeroSection.displayName = 'HeroSection';

export default HeroSection;
